import React, { useEffect, useId, useRef, useState } from 'react';
import { searchManualOrderAddressBook } from '../../services/shipturtleApi';

/**
 * "Fill from an existing customer or vendor" for the manual-order wizard.
 *
 * A typeable, searchable list over the address book. Picking an entry hands
 * its values to the wizard, which writes them into the ordinary fields — all
 * still editable, and stored on the order exactly as if typed. Nothing links
 * the order to the entry afterwards.
 *
 * Controlled: the wizard owns `kind` and `picked` ({ entry, addressIndex }),
 * so the choice survives moving between steps — this unmounts whenever its
 * step is not showing.
 */

const KIND_COPY = {
  customer: {
    placeholder: 'Search customers by name, email or phone…',
    tooShort: 'Type at least 2 characters — a name, email or phone number.',
    empty: 'No customer matches. Fill the fields below by hand.',
  },
  vendor: {
    placeholder: 'Search vendors by name, warehouse, city or pincode…',
    tooShort: '',
    empty: 'No vendor warehouse matches. Fill the fields below by hand.',
  },
};

const MIN_QUERY = { customer: 2, vendor: 0 };

const addressSummary = (address) =>
  [address.address_line_1, address.city, address.pincode].filter(Boolean).join(', ');

export default function PartyLookup({
  title,
  kinds,
  kind,
  onKindChange,
  picked,
  onPick,
  onAddress,
  onClear,
}) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [active, setActive] = useState(-1);
  const rootRef = useRef(null);

  const copy = KIND_COPY[kind];
  const tooShort = query.trim().length < MIN_QUERY[kind];

  // Debounced, and each request aborts the one before it, so a slow early
  // response can never overwrite the list for what is typed now.
  useEffect(() => {
    if (!open) return undefined;
    if (tooShort) {
      setResults([]);
      setLoading(false);
      setError('');
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await searchManualOrderAddressBook(
          { kind, q: query.trim() },
          { signal: controller.signal }
        );
        setResults(data?.results || []);
        setError('');
        setActive(-1);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setResults([]);
        setError(err?.message || 'Could not search. Fill the fields by hand.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, kind, query, tooShort]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  const choose = (entry) => {
    setOpen(false);
    setQuery('');
    setActive(-1);
    onPick(entry);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      // Never submits the wizard from here; Enter picks the highlighted row.
      e.preventDefault();
      if (open && results[active]) choose(results[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const addresses = picked?.entry.addresses || [];

  return (
    <div className="partyLookup" ref={rootRef}>
      <div className="partyLookupHead">
        <span className="partyLookupTitle">{title}</span>
        {kinds.length > 1 && (
          <div className="partyLookupTabs" role="tablist" aria-label="Look up from">
            {kinds.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={kind === key}
                className={`partyLookupTab ${kind === key ? 'isActive' : ''}`}
                onClick={() => {
                  if (kind === key) return;
                  setQuery('');
                  setResults([]);
                  setOpen(false);
                  onKindChange(key);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="partyLookupBox">
        <input
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          className="partyLookupInput"
          value={query}
          placeholder={copy.placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />

        {open && (
          <ul className="partyLookupList" role="listbox" id={listId}>
            {tooShort && copy.tooShort && <li className="partyLookupNote">{copy.tooShort}</li>}
            {!tooShort && loading && <li className="partyLookupNote">Searching…</li>}
            {!tooShort && !loading && error && (
              <li className="partyLookupNote isError">{error}</li>
            )}
            {!tooShort && !loading && !error && results.length === 0 && (
              <li className="partyLookupNote">{copy.empty}</li>
            )}
            {!loading &&
              results.map((entry, index) => (
                <li
                  key={entry.id}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === active}
                  className={`partyLookupOption ${index === active ? 'isActive' : ''}`}
                  // mousedown, not click: keeps focus in the input, so the
                  // outside-click handler never sees the pick as a dismissal.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(entry);
                  }}
                  onMouseEnter={() => setActive(index)}
                >
                  <span className="partyLookupOptionLabel">{entry.label}</span>
                  {entry.detail && (
                    <span className="partyLookupOptionDetail">{entry.detail}</span>
                  )}
                </li>
              ))}
          </ul>
        )}
      </div>

      {picked && (
        <div className="partyLookupPicked">
          <span>
            Filled from <strong>{picked.entry.label}</strong>. Every field below stays editable.
          </span>
          <button type="button" className="partyLookupClear" onClick={onClear}>
            Clear fields
          </button>
        </div>
      )}

      {/* A customer may have shipped to several places; the latest leads, the
          others are a pick away rather than retyped. */}
      {addresses.length > 1 && (
        <label className="field partyLookupAddresses">
          <span>Saved addresses ({addresses.length})</span>
          <select
            value={picked.addressIndex}
            onChange={(e) => onAddress(Number(e.target.value))}
          >
            {addresses.map((address, index) => (
              <option key={addressSummary(address)} value={index}>
                {index === 0 ? 'Latest · ' : ''}
                {addressSummary(address)}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
